import { createContext, use, useEffect, useMemo, useRef, useState } from 'react'
import Stories from 'react-insta-stories'

import { useTheme } from '@/shared/hooks/use-theme'
import { Skeleton } from '@/shared/ui'

import type { ICustomStoriesProps, IStorySlideProps, TStoryAction } from './types'

import styles from './custom-stories.module.scss'

const progressContainerStyles: React.CSSProperties = {
    width: '100%',
    alignItems: 'center',
    alignSelf: 'flex-start',
    height: '56px',
    paddingRight: '16px',
    paddingLeft: '16px',
    paddingTop: '0px',
    paddingBottom: '0px',
    filter: 'none',
}

const progressStyles: React.CSSProperties = {
    height: '2px',
    backgroundColor: 'var(--text-and-icon-primary)',
}

const progressWrapperStyles: React.CSSProperties = {
    height: '2px',
    backgroundColor: 'var(--background-tertiary)',
}

const StoryActionContext = createContext<null | React.RefObject<null | TStoryAction>>(null)

export const CustomStoriesSlide = ({ action, onActionUpdate, children }: IStorySlideProps) => {
    const actionRef = use(StoryActionContext)

    useEffect(() => {
        if (actionRef) {
            actionRef.current = action
        }

        onActionUpdate?.(action)
    }, [action, actionRef, onActionUpdate])

    return children
}

const storiesLoader = <Skeleton />

export const CustomStories = ({
    setShowStories,
    stories: createStories,
}: ICustomStoriesProps): React.ReactNode => {
    const [currentIndex, setCurrentIndex] = useState(0)
    const currentActionRef = useRef<null | TStoryAction>(null)
    const holdTimeoutRef = useRef<null | number>(null)
    const isLongPressRef = useRef(false)
    const { theme } = useTheme()

    const stories = useMemo(
        () =>
            createStories({
                setShowStories,
                setCurrentIndex,
                theme,
            }),
        [createStories, setShowStories, setCurrentIndex, theme],
    )

    const handlePointerDown = () => {
        isLongPressRef.current = false
        holdTimeoutRef.current = globalThis.setTimeout(() => {
            isLongPressRef.current = true
        }, 300)

        currentActionRef.current?.('pause')
    }

    const handlePointerUp = (direction: 'next' | 'prev') => {
        if (holdTimeoutRef.current) {
            clearTimeout(holdTimeoutRef.current)
            holdTimeoutRef.current = null
        }

        currentActionRef.current?.('play')

        if (!isLongPressRef.current) {
            if (direction === 'prev') {
                setCurrentIndex((index) => Math.max(index - 1, 0))
            } else if (currentIndex < stories.length - 1) {
                setCurrentIndex((index) => index + 1)
            } else {
                setShowStories()
            }
        }
    }

    return (
        <StoryActionContext value={currentActionRef}>
            <div className={styles['custom-stories']}>
                <Stories
                    currentIndex={currentIndex}
                    defaultInterval={5000}
                    height={window.innerHeight}
                    loader={storiesLoader}
                    preventDefault={true}
                    progressContainerStyles={progressContainerStyles}
                    progressStyles={progressStyles}
                    progressWrapperStyles={progressWrapperStyles}
                    stories={stories}
                    width={window.innerWidth}
                    onAllStoriesEnd={setShowStories}
                />
                <div className={styles['custom-stories__overlay']}>
                    <div
                        className={styles['custom-stories__overlay_left']}
                        onPointerDown={handlePointerDown}
                        onPointerUp={() => handlePointerUp('prev')}
                    />
                    <div
                        className={styles['custom-stories__overlay_right']}
                        onPointerDown={handlePointerDown}
                        onPointerUp={() => handlePointerUp('next')}
                    />
                </div>
            </div>
        </StoryActionContext>
    )
}
